'use client';

import React, { useCallback } from 'react';
import { Bell, MapPin, Plus, ScrollText, Search, User } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/components/context/auth-context';
import type { AccountDto } from '@/libs/api/account';
import useRedirect from '@/libs/hooks/use-redirect';
import styles from './styles.module.scss';

type NavigationKey =
  | 'feed'
  | 'nearby'
  | 'search'
  | 'account'
  | 'notifications'
  | 'write';

export interface TopNavigationViewProps {
  pathname: string;
  isAuthenticated: boolean;
  isAuthLoading?: boolean;
  account?: AccountDto;
  onLogin: () => void;
}

const iconProps = {
  width: 24,
  height: 24,
  strokeWidth: 1.5,
  'aria-hidden': true,
} as const;

// 피드 상세는 어느 탭도 선택하지 않는다(lone-web-pc pc-v2-handoff).
// 프로필은 내 계정일 때만 '내 계정'을 선택 표시한다.
const getActiveNavigation = (pathname: string, account?: AccountDto): NavigationKey | undefined => {
  if (pathname.startsWith('/feeds/write')) return 'write';
  if (pathname === '/') return 'feed';
  if (pathname.startsWith('/nearby')) return 'nearby';
  if (pathname.startsWith('/search')) return 'search';
  if (pathname.startsWith('/notifications')) return 'notifications';
  if (pathname.startsWith('/settings') || pathname.startsWith('/auth/account/create')) return 'account';
  if (account && pathname.startsWith(`/accounts/${encodeURIComponent(account.name)}`)) return 'account';
  return undefined;
};

/**
 * PC(769px 이상) 상단 고정 내비게이션. 로고 · 가운데 60px 알약 탭 · 알림/작성 아이콘 버튼으로 이뤄진다.
 * 769–1023px에서는 탭 라벨을 숨기고 아이콘과 접근 가능한 이름만 남긴다.
 */
export const TopNavigationView = ({
  pathname,
  isAuthenticated,
  isAuthLoading = false,
  account,
  onLogin,
}: TopNavigationViewProps) => {
  const activeNavigation = getActiveNavigation(pathname, account);
  const accountHref = account ? `/accounts/${encodeURIComponent(account.name)}` : '/auth/account/create';

  const tabClassName = (key: NavigationKey) =>
    `${styles.tab} ${activeNavigation === key ? styles.tabActive : ''}`;

  const renderTab = (key: NavigationKey, href: string, label: string, icon: React.ReactNode) => (
    <Link
      key={key}
      href={href}
      className={tabClassName(key)}
      aria-label={label}
      aria-current={activeNavigation === key ? 'page' : undefined}
      title={label}
    >
      {icon}
      <span className={styles.tabLabel}>{label}</span>
    </Link>
  );

  return (
    <nav className={styles.bar} aria-label="주요 메뉴">
      <div className={styles.inner}>
        <div className={styles.start}>
          <Link href="/" className={styles.logo} aria-label="로네 홈">로네</Link>
        </div>

        <div className={styles.tabs}>
          {renderTab('feed', '/', '피드', <ScrollText {...iconProps} />)}
          {renderTab('nearby', '/nearby', '주변', <MapPin {...iconProps} />)}
          {renderTab('search', '/search', '검색', <Search {...iconProps} />)}
          {isAuthLoading ? (
            <span className={`${styles.tab} ${styles.placeholder}`} aria-hidden="true">
              <User {...iconProps} />
              <span className={styles.tabLabel}>내 계정</span>
            </span>
          ) : isAuthenticated ? (
            renderTab('account', accountHref, '내 계정', <User {...iconProps} />)
          ) : (
            <button type="button" className={styles.tab} onClick={onLogin} aria-label="로그인" title="로그인">
              <User {...iconProps} />
              <span className={styles.tabLabel}>로그인</span>
            </button>
          )}
        </div>

        <div className={styles.end}>
          {isAuthenticated ? (
            <Link
              href="/notifications"
              className={`${styles.iconButton} ${activeNavigation === 'notifications' ? styles.iconButtonActive : ''}`}
              aria-label="알림"
              aria-current={activeNavigation === 'notifications' ? 'page' : undefined}
              title="알림"
            >
              <Bell {...iconProps} />
            </Link>
          ) : null}
          <Link
            href="/feeds/write"
            className={`${styles.iconButton} ${activeNavigation === 'write' ? styles.iconButtonActive : ''}`}
            aria-label="새 피드 작성"
            aria-current={activeNavigation === 'write' ? 'page' : undefined}
            title="새 피드 작성"
          >
            <Plus {...iconProps} />
          </Link>
        </div>
      </div>
    </nav>
  );
};

const TopNavigation = () => {
  const auth = useAuth();
  const router = useRouter();
  const redirect = useRedirect();
  const pathname = usePathname();

  const handleLoginClick = useCallback(() => {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/auth/sign-in') {
      redirect.set(currentPath);
    }
    router.push('/auth/sign-in');
  }, [redirect, router]);

  return (
    <TopNavigationView
      pathname={pathname}
      isAuthenticated={Boolean(auth.me)}
      isAuthLoading={auth.isLoading}
      account={auth.me?.activeAccount}
      onLogin={handleLoginClick}
    />
  );
};

export default TopNavigation;
